import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './idb-button.css.lit';
import { OutlineButton } from '../../base/outline-button/outline-button';
import { LinkTargetType } from '../../base/outline-link/config';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { getUrlTarget } from '../../../utilities/url_utils';
import '../idb-icon/idb-icon';

export type ButtonVariant =
  | 'none'
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'quinary';

export type ButtonSize = 'small' | 'medium' | 'large';

export type CurrentValues =
  | 'page'
  | 'step'
  | 'location'
  | 'date'
  | 'time'
  | 'true'
  | 'false';

/**
 * The IdbButton component
 * @element idb-button
 * @slot idb-button--content - content slot.
 */
@customElement('idb-button')
export class IdbButton extends OutlineButton {
  static styles: CSSResultGroup = [componentStyles];

  @query('.btn')
  button: HTMLButtonElement;

  /**
   * The url to use for a link. This will render an anchor element.
   * Do not set this prop if you want to render a button element.
   */
  @property({ type: String, attribute: 'button-url' })
  buttonUrl: string;

  /**
   * ARIA label attribute to pass down to the resulting button or a
   * element. This is required for accessibility if we use a button
   * with an icon only.
   */
  @property({ type: String, attribute: 'button-label' })
  buttonLabel: string;

  /**
   * The target to use for a link, used in conjunction with the url attribute.
   */
  @property({ type: String, attribute: 'button-target' })
  buttonTarget: LinkTargetType;

  /**
   * The button style variant to use.
   */
  @property({ type: String, attribute: 'button-variant' })
  buttonVariant: ButtonVariant = 'primary';

  /**
   * The button size to use.
   */
  @property({ type: String, attribute: 'button-size' })
  buttonSize: ButtonSize = 'medium';

  /**
   * The (optional) icon to use on left side of button.
   */
  @property({ type: String, attribute: 'icon-left' })
  iconLeft: string | boolean = false;

  /**
   * The (optional) icon to use on right side of button.
   */
  @property({ type: String, attribute: 'icon-right' })
  iconRight: string | boolean = false;

  /**
   * Whether the button is disabled. Only applies to
   * implementations not using the url property
   */
  @property({ type: Boolean, attribute: 'is-disabled' })
  isDisabled = false;

  /**
 * Whether the button is rounded. Only applies to
 * implementations not using the url property
 */
  @property({ type: Boolean, attribute: 'rounded' })
  isRounded = false;

  // Center the anchor text within the button
  @property({ type: Boolean, attribute: 'center-items' })
  centerItems = false;

  /**
   * Whether the button is disabled. Only applies to
   * implementations not using the url property
   */
  @property({ type: Boolean, attribute: 'icon-only' })
  iconOnly = false;

  /**
   * Whether the button is active. Applies the hover/focus state color to the button.
   */
  @property({ type: Boolean, attribute: 'active' })
  active = false;

  /**
   * For use in rare cases such as navigation when you need to pass an aria-current rent value such as 'page' or 'item'.
   */
  @property({ type: String })
  current: CurrentValues;

  /**
   * A click handler to be passed only to onClick. DO NOT USE @click on this component.
   */
  @property() onClick: () => void;

  /**
   * A keyUp handler to be passed to the onKeyUp. DO NOT USE @keyup on this component.
   */
  @property() onKeyUp: () => void;

  firstUpdated(): void {
    const link = this.querySelector('a');
    if (link) {
      this.button.setAttribute('tabindex', '-1');
      this.addEventListener('click', () => {
        link.click();
      });
    }
  }

  updated() {
    // checks the is-disabled prop and manages aria-disabled attributes on the <outline-button> element itself.
    if (this.hasAttribute('is-disabled')) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.setAttribute('aria-disabled', 'false');
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    const link = this.querySelector('a');
    if (link) {
      this.removeEventListener('click', () => {
        link.click();
      });
    }
  }

  /**
   * Component render function
   * @returns TemplateResult
   */
  render(): TemplateResult {
    if (this.buttonUrl) {
      this.buttonTarget = getUrlTarget(this.buttonUrl, this.buttonTarget);
    }
    const buttonClasses = {
      'btn': true,
      [this.buttonVariant]: true,
      [this.buttonSize]: true,
      'active': this.active,
      'grid-button-wrapper': this.centerItems,
      'rounded': this.isRounded,
    };

    const iconOnlyClasses = {
      'icon-only': this.iconOnly,
      'grid-button': this.centerItems,
      ...buttonClasses,
    };

    return this.buttonUrl
      ? html` <a
          class="${classMap(iconOnlyClasses)}"
          href="${this.buttonUrl}"
          target="${this.buttonTarget}"
          aria-label="${ifDefined(this.buttonLabel)}"
          aria-disabled="${this.isDisabled}"
          aria-current="${ifDefined(this.current)}"
        >
          ${this.buttonTemplate()}
        </a>`
      : html`<button
          class="${classMap(buttonClasses)}"
          aria-disabled="${this.isDisabled}"
          aria-label="${ifDefined(this.buttonLabel)}"
          .onclick="${this.onClick}"
          .onkeyup="${this.onKeyUp}"
        >
          ${this.buttonTemplate()}
        </button> `;
  }

  /**
   * Method to return the html required to render the button contents.
   *
   * @returns TemplateResult
   */
  buttonTemplate(): TemplateResult {
    return html`
        ${
          this.iconLeft
            ? html`<idb-icon class="left" icon="${this.iconLeft}" />`
            : null
        }
        <slot></slot>
        ${
          this.iconRight
            ? html`<idb-icon class="right" icon="${this.iconRight}" />`
            : null
        }
      `;
  }
}
